import { useState } from 'react';
import { cn } from '@mlm/ui';
import { storefrontAssetUrl } from '../../lib/api';

/**
 * A persona's face: the uploaded avatar image when the tenant set one,
 * otherwise its emoji. Deliberately NOT `@mlm/ui`'s Avatar — that one's
 * fallback uppercases and slices to two characters, which mangles an emoji.
 *
 * An image that fails to load (key removed, asset route 404) falls back to the
 * emoji rather than leaving a broken-image box.
 */
const SIZES = {
  sm: { box: 'h-6 w-6', text: 'text-lg' },
  md: { box: 'h-8 w-8', text: 'text-xl' },
  lg: { box: 'h-10 w-10', text: 'text-2xl' },
  xl: { box: 'h-12 w-12', text: 'text-3xl' },
} as const;

export function PersonaAvatar({
  icon,
  avatarKey,
  name,
  size = 'md',
  className,
}: {
  icon: string;
  avatarKey?: string | null;
  /** Persona name — alt text when an image is shown. */
  name?: string;
  size?: keyof typeof SIZES;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);
  const s = SIZES[size];

  if (avatarKey && !failed) {
    return (
      <img
        src={storefrontAssetUrl(avatarKey)}
        alt={name ?? ''}
        onError={() => setFailed(true)}
        className={cn('flex-none rounded-full object-cover', s.box, className)}
      />
    );
  }

  return (
    <span className={cn('flex-none leading-none', s.text, className)} aria-hidden>
      {icon}
    </span>
  );
}
